import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { buildLoyaltySyncSignature, nextLoyaltySyncState } from "../redemption";
import { selectLoyaltyDiscount, selectSaleData } from "../selectors";

export interface CheckoutLoyaltySyncOptions {
  /** Whether the checkout is on screen. Closing it forgets the signature — see the rule. */
  isOpen: boolean;
  /**
   * Bring the payment snapshot back in step with the sale.
   *
   * Called with `isResync: false` on the first pass with the checkout open (the mount-time prep)
   * and `true` when points changed while it stayed open. Both must prepare the snapshot; only a
   * resync additionally needs to re-seed whatever the platform derives from the balance, because
   * on a resync the screen already has a selection and an amount from the PREVIOUS total.
   */
  onSync: (isResync: boolean) => void;
}

/**
 * Keep a checkout's payment snapshot in step with the loyalty points on the bill.
 *
 * 🔴 **This exists because a checkout's payment state is a SNAPSHOT and the loyalty panel lives
 * inside it.** `ChargableMethods` mounts the redemption panel, so Apply / Remove / Change amount
 * all mutate the sale without the checkout ever closing — and the mount-time prep those screens
 * do keys on the modal's own open flag, which never re-toggles. The result is a screen charging
 * the pre-points total: `apps/web` hit that and fixed it inline, and `apps/mobile` still had it
 * until 2026-09-08, where its prep effect keyed on `checkoutModalState` alone.
 *
 * ```ts
 * useCheckoutLoyaltySync({
 *   isOpen: !!checkoutModalStore?.checkoutModalState,
 *   onSync: (isResync) => {
 *     prepareLocalPaymentInfo(checkoutContextState, setCheckoutContextState);
 *     if (isResync) { ...re-seed the leg... }
 *   },
 * });
 * ```
 *
 * The rule — which figures make a signature, and initial-open versus in-place resync versus the
 * reset on close — is `buildLoyaltySyncSignature` + `nextLoyaltySyncState`, unit-tested. What a
 * sync DOES stays with the host: `prepareLocalPaymentInfo` is platform-divergent (different
 * arities, and only one of the two mutates the context object in place), so this hook decides
 * WHEN and leaves WHAT to the caller.
 *
 * ⚠️ **`onSync` is held on a ref and is deliberately NOT an effect dependency.** Hosts pass an
 * inline closure over their checkout context; depending on it would re-run the sync on every
 * render and re-prep the screen continuously.
 */
export function useCheckoutLoyaltySync({ isOpen, onSync }: CheckoutLoyaltySyncOptions): void {
  const discount = useSelector(selectLoyaltyDiscount);
  const saleData = useSelector(selectSaleData);
  const signature = buildLoyaltySyncSignature(discount, saleData?.billRoundedAmount);

  const onSyncRef = useRef(onSync);
  // Declared BEFORE the sync effect on purpose: effects run in declaration order, so the ref is
  // current by the time the sync below reads it, even when both change in one commit.
  useEffect(() => {
    onSyncRef.current = onSync;
  }, [onSync]);

  const prevSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    const { prevSignature, action } = nextLoyaltySyncState(prevSignatureRef.current, {
      isOpen,
      signature,
    });
    prevSignatureRef.current = prevSignature;
    if (action === "none") return;
    onSyncRef.current(action === "resync");
  }, [isOpen, signature]);
}
